import React, { useEffect, useState } from "react";
import API from "../../api/axiosConfig";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { Pencil, Trash, Eye, EyeOff, ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CreateProductModal from "../../components/modals/CreateProductModal";
import { getImageUrl } from "../../utils/getImageUrl";

const getId = (obj) => obj?.id ?? obj?._id;
const PAGE_SIZE = 20;

export default function CompanyPublications() {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));
  const companyId = user?.company?.id ?? user?.company?._id;
  const navigate = useNavigate();

  const fetchProducts = async (targetPage = page) => {
    try {
      const res = await API.get(`/products`, { params: { companyId, page: targetPage, limit: PAGE_SIZE } });
      const raw = res.data?.data?.data ?? res.data?.data?.products ?? res.data?.products ?? res.data?.data ?? res.data;
      setProducts(Array.isArray(raw) ? raw : []);
      setTotal(res.data?.data?.total ?? 0);
      setPages(res.data?.data?.pages ?? 1);
    } catch (err) {
      console.log(err);
      toast.error("Error al cargar publicaciones");
    }
  };

  useEffect(() => {
    fetchProducts(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const toggleVisibility = async (id, currentState) => {
    try {
      await API.put(`/products/${id}`, { visible: !currentState });
      toast.success("Estado actualizado");
      fetchProducts();
    } catch (err) {
      toast.error("Error al cambiar visibilidad");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar esta publicación?")) return;
    try {
      await API.delete(`/products/${id}`);
      toast.success("Publicación eliminada");
      fetchProducts();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <button onClick={() => navigate(-1)}
        className="flex items-center gap-2 mb-4 text-supernova-600 hover:text-supernova-700">
        <ArrowLeft size={20} /> Volver
      </button>

      <h2 className="text-3xl font-bold mb-6 text-center">Mis publicaciones</h2>

      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-gray-500">{total} publicación{total !== 1 ? "es" : ""} en total</span>
        <button onClick={() => { setEditingProduct(null); setShowCreateModal(true); }}
          className="px-4 py-2 bg-supernova-500 text-white rounded-lg hover:bg-supernova-700">
          Crear publicación
        </button>
      </div>

      <div className="overflow-x-auto bg-white shadow rounded-xl border">
        <table className="w-full text-left">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="p-3">Imagen</th>
              <th className="p-3">Título</th>
              <th className="p-3">Precio</th>
              <th className="p-3">Sedes</th>
              <th className="p-3">Sección</th>
              <th className="p-3">Estado</th>
              <th className="p-3 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center p-5 text-gray-500">
                  No hay publicaciones registradas.
                </td>
              </tr>
            ) : (
              products.map((p) => (
                <motion.tr key={getId(p)} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="border-b">
                  <td className="p-3">
                    {p.images?.length > 0 ? (
                      <img src={getImageUrl(p.images[0])} alt={p.title}
                        className="w-16 h-16 rounded-lg object-cover border" />
                    ) : (
                      <div className="w-16 h-16 rounded bg-supernova-100 flex items-center justify-center text-supernova-700 font-semibold text-xs">
                        Sin imagen
                      </div>
                    )}
                  </td>
                  <td className="p-3 font-semibold">{p.title}</td>
                  <td className="p-3 text-supernova-700 font-bold">
                    ${Number(p.price).toLocaleString("es-CO")}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {p.productSedes?.length > 0 ? (
                        p.productSedes.map((ps) => (
                          <span key={getId(ps.sede ?? ps)} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                            {ps.sede?.name ?? ps.name ?? "—"}
                          </span>
                        ))
                      ) : p.sedes?.length > 0 ? (
                        p.sedes.map((s) => (
                          <span key={getId(s)} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                            {s.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    {p.seccion ? (
                      <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
                        {p.seccion.name}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    {p.visible ? (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">Activo</span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">Inactivo</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-3 justify-center">
                      <button onClick={() => { setEditingProduct(p); setShowCreateModal(true); }}
                        className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">
                        <Pencil size={18} />
                      </button>
                      <button onClick={() => toggleVisibility(getId(p), p.visible)}
                        className="p-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200">
                        {p.visible ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                      <button onClick={() => deleteProduct(getId(p))}
                        className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                        <Trash size={18} />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>

        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} /> Anterior
            </button>
            <span className="text-sm text-gray-500">Página {page} de {pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Siguiente <ChevronRight size={16} />
            </button>
          </div>
        )}

        <CreateProductModal
          open={showCreateModal}
          onClose={() => { setShowCreateModal(false); setEditingProduct(null); }}
          onCreated={fetchProducts}
          productToEdit={editingProduct}
        />
      </div>
    </div>
  );
}
